import React from "react";
import { Controller, useForm } from "react-hook-form";
import styles from "../styles.module.scss";
import ModalLayout from "app/components/Layouts/ModalLayout/ModalLayout";
import CloseIcon from "../../../../public/images/close.svg";
import { useTranslation } from "react-i18next";
import Select from "react-select";
import ButtonVariant from "app/components/Button/ButtonVariant";
import TextInput from "app/components/inputs/TextInput";

interface User {
  id: string;
  firstName: string;
  lastName: string;
  title?: string;
  email: string;
  role: string;
  isAdmin: boolean;
}

interface AddFacilityUser {
  isOpen: boolean;
  onClose: () => void;
  users: User[];
  onAddUser: (user: User) => void;
}

const DEFAULT_COACH_VALUE = "coach";

// Both roles become a coach profile, which is the only member a title is stored on.
const isCoachRole = (role: string) => role === "coach" || role === "coach_admin";

const AddFacilityUser = ({
  isOpen,
  onClose,
  users,
  onAddUser,
}: AddFacilityUser) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    control,
    trigger,
    setError,
    watch,
  } = useForm<User>({
    defaultValues: {
      role: DEFAULT_COACH_VALUE,
    },
  });
  const { t } = useTranslation();

  const emailIsUnique = (email: string) => {
    return !users.find((user: User) => user.email === email);
  };

  const onSubmit = (data: User) => {
    const trimmedFirstName = data.firstName?.trim() || null;
    const trimmedLastName = data.lastName?.trim() || null;
    console.log("data.role: ", data.role);

    if (!emailIsUnique(data.email)) {
      setError("email", {
        type: "manual",
        message: t("Email is already in use"),
      });
    } else if (!trimmedFirstName) {
      setError("firstName", {
        type: "manual",
        message: t("First name is required"),
      });
    } else if (!trimmedLastName) {
      setError("lastName", {
        type: "manual",
        message: t("Last name is required"),
      });
    } else {
      const formatedData = {
        ...data,
        firstName: trimmedFirstName,
        lastName: trimmedLastName,
        // Only a coach carries a title, so a title typed before switching the role away
        // from Coach is dropped rather than sent for a role that cannot hold one.
        title: isCoachRole(data.role) ? data.title?.trim() || "" : "",
        isAdmin: data.role === "coach_admin",
        role: data.role === "coach_admin" ? "coach" : data.role,
      };

      onAddUser(formatedData);
      onClose();
      reset({
        firstName: "",
        lastName: "",
        title: "",
        email: "",
        role: DEFAULT_COACH_VALUE,
        isAdmin: false,
      });
    }
  };

  const IndicatorSeparator = (): JSX.Element | null => null;

  const roles = [
    { value: "admin", label: t("Admin"), isAdmin: false },
    { value: "coach", label: t("Coach"), isAdmin: false },
    { value: "coach_admin", label: t("Coach - Admin"), isAdmin: true },
    { value: "scheduler", label: t("Scheduler"), isAdmin: false },
  ];

  return (
    <ModalLayout isOpen={isOpen} onClose={onClose} classNames={styles.modal}>
      <div className={styles.modal_header}>
        <h2>{t("Add User")}</h2>
        <img
          className={styles.modal_status_icon}
          src={CloseIcon}
          alt="info"
          onClick={onClose}
        />
      </div>
      <div className={styles.modal_content}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <label htmlFor="role">{t("User Type")}</label>
          <div style={{ marginBottom: "10px" }}>
            <Controller
              control={control}
              name="role"
              render={({ field }) => (
                <Select
                  id="role"
                  value={
                    roles.find((option) => option.value === field.value) ||
                    roles[1]
                  }
                  onChange={(selectedOption) => {
                    field.onChange(
                      (selectedOption as { value: string; label: string })
                        ?.value || "",
                    );
                    trigger("role");
                  }}
                  options={roles}
                  components={{ IndicatorSeparator }}
                  placeholder={t("Select Role")}
                  isSearchable={false}
                />
              )}
            />
          </div>
          <div className={styles.form_row}>
            <TextInput label={t("First Name")} {...register("firstName", {required: t("First name is required")})} />
            {errors.firstName && <span>{errors.firstName.message}</span>}
          </div>
          <div className={styles.form_row}>
            <TextInput label={t("Last Name")} {...register("lastName", {required: t("Last name is required")})} />
            {errors.lastName && <span>{errors.lastName.message}</span>}
          </div>
          {isCoachRole(watch("role")) && (
            <div className={styles.form_row}>
              <TextInput label={t("Title")} {...register("title")} />
            </div>
          )}
          <div className={styles.form_row}>
            <TextInput
              label={t("Email")}
              {...register("email", {
                required: t("Email is required"),
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: t("Invalid email address")
                }
              })}
            />
            {errors.email && <span>{errors.email.message}</span>}
          </div>
          <div className={styles.buttonGroup}>
            <ButtonVariant
              title={t("Cancel")}
              buttonType='secondary-outline'
              onClickFunc={onClose}
              buttonStyles={styles.button_116_width}
            />
            <ButtonVariant
              title={t("Save")}
              buttonType='primary'
              onClickFunc={handleSubmit(onSubmit)}
              buttonStyles={styles.button_116_width}
            />
          </div>
        </form>
      </div>
    </ModalLayout>
  );
};

export default AddFacilityUser;
